/**
 * PaymentsWidget — painter dashboard financial snapshot.
 *
 * Renders "Payments Awaiting Action" — counts of requested / overdue /
 * outstanding balance. Clicking the widget navigates to /pro/jobs so the
 * painter can drill into any job's Payments tab.
 *
 * Gracefully hidden when the painter has no Job Workflow access (403/404).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Receipt, AlertTriangle, Send, DollarSign, ChevronRight } from "lucide-react";
import api from "@/lib/api";

export default function PaymentsWidget() {
  const [state, setState] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get("/pro/payments/summary")
      .then((r) => { if (!cancelled) setState(r.data); })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, []);

  // Access denied or no data yet — render nothing.
  if (error || !state) return null;

  const { requested_count = 0, overdue_count = 0, not_requested_count = 0,
          outstanding_aud = 0, job_count = 0 } = state;
  // Only show the widget when the painter actually has jobs.
  if (job_count === 0) return null;

  const anyAction = overdue_count > 0 || requested_count > 0 || outstanding_aud > 0;

  return (
    <Link
      to="/pro/jobs"
      data-testid="payments-widget"
      className={`block bg-white border ${anyAction ? "border-brand-linen" : "border-brand-linen"} hover:border-brand-teal transition-colors p-5`}
    >
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <div className="overline text-brand-teal mb-1">Field ops</div>
          <div className="font-serif text-xl text-brand-ink flex items-center gap-2">
            <Receipt className="w-5 h-5 text-brand-teal" />
            Payments awaiting action
          </div>
        </div>
        <ChevronRight className="w-4 h-4 text-brand-muted mt-1 shrink-0" />
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Stat
          icon={AlertTriangle}
          label="Overdue"
          value={overdue_count}
          tint={overdue_count > 0 ? "rose" : "muted"}
          testid="payments-widget-overdue"
        />
        <Stat
          icon={Send}
          label="Requested"
          value={requested_count}
          tint={requested_count > 0 ? "amber" : "muted"}
          testid="payments-widget-requested"
        />
        <Stat
          icon={DollarSign}
          label="Outstanding"
          value={`$${Number(outstanding_aud).toFixed(0)}`}
          tint={outstanding_aud > 0 ? "ink" : "muted"}
          testid="payments-widget-outstanding"
        />
      </div>

      {not_requested_count > 0 && (
        <div className="mt-3 text-xs text-brand-muted">
          {not_requested_count} milestone{not_requested_count === 1 ? "" : "s"} not yet requested.
        </div>
      )}
    </Link>
  );
}

function Stat({ icon: Icon, label, value, tint, testid }) {
  const tintClass =
    tint === "rose" ? "text-rose-700 bg-rose-50 border-rose-200" :
    tint === "amber" ? "text-amber-800 bg-amber-50 border-amber-200" :
    tint === "ink" ? "text-brand-ink bg-white border-brand-linen" :
    "text-brand-muted bg-stone-50 border-brand-linen";
  return (
    <div className={`border p-3 ${tintClass}`} data-testid={testid}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] uppercase tracking-wide font-medium">{label}</span>
        <Icon className="w-3.5 h-3.5" />
      </div>
      <div className="font-serif text-2xl leading-none">{value}</div>
    </div>
  );
}
